"use client";

/**
 * The single rendering surface for every `toast()` call across the app.
 * Mounted once in `app/layout.tsx`; subscribes to the singleton store
 * via `useToast()` and projects each entry into a Radix `<Toast>`
 * inside the `<ToastViewport>` (positioned bottom-right by the
 * primitive in `components/ui/toast.tsx`).
 *
 * Why this is a separate file from `toast.tsx`: the lower-level file
 * exports the Radix wrappers (Provider, Viewport, Toast, Title, …) as
 * primitives. This file is the *consumer* — it ties the singleton store
 * to those primitives, and it's the only thing the root layout needs
 * to import. Splitting them lets specialised callers (a future inline
 * `<Toaster scoped />`, e.g. inside a modal) reuse the primitives
 * without dragging the global store along.
 *
 * Auto-dismiss duration: 5000ms across the board, set via Radix's
 * `duration` prop on the provider below. Per-toast `duration` can
 * still be overridden on the `toast()` call (e.g., a long-running
 * "undo" toast might want 10s) but the default we ship is uniform.
 * When the user mouses over the toast, Radix pauses the timer and
 * resumes on mouse-out — good default for read-while-they-stack
 * behaviour.
 */

import * as React from "react";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { useToast } from "@/lib/use-toast";

const DEFAULT_TOAST_DURATION_MS = 5_000;

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider duration={DEFAULT_TOAST_DURATION_MS} swipeDirection="right">
      {toasts.map(({ id, title, description, action, ...props }) => (
        <Toast key={id} {...props}>
          <div className="grid gap-1">
            {title && <ToastTitle>{title}</ToastTitle>}
            {description && (
              <ToastDescription>{description}</ToastDescription>
            )}
          </div>
          {action}
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
